import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  Download, 
  FileText, 
  ChevronDown, 
  ChevronUp,
  Info,
  Calendar,
  Settings,
  Filter
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { CreateReportModal } from './CreateReportModal';

interface Report {
  id: string;
  name: string;
  description: string;
  category: 'accounting' | 'billing' | 'consumption' | 'location' | 'analytics' | 'compliance';
  deliveryOptions: string[];
  deliveryMethods: string[];
}

const reports: Report[] = [
  {
    id: 'account-ids',
    name: 'Account IDs Report',
    description: 'Shows a listing of all vendor billing IDs, service account IDs, and meter IDs for all utilities, for each location.',
    category: 'accounting',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'accrual-report',
    name: 'Accrual Report Most Recent Bill Method',
    description: 'This report helps calculate the amount accrued (the unbilled portion of the bills) for a specified date range or till a specified fiscal end date.',
    category: 'accounting',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'bill-details',
    name: 'Bill Details Report',
    description: 'Provides details about charges and usage for all meters (accounts) associated with your bills. Use this report to get structured data extracts of your bills.',
    category: 'billing',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'bill-errors',
    name: 'Bill Errors Report',
    description: 'This Report provides a listing of all the Bill Errors that were encountered while processing a bill along with the PearID and their Current Status.',
    category: 'billing',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'billing-accounts',
    name: 'Billing Accounts Report',
    description: 'Shows a listing of all the Billing accounts and their attributes.',
    category: 'billing',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'billing-line-items',
    name: 'Billing Line Items Report',
    description: 'Provides details about charges and usage for all virtual accounts associated with your bills. Use this report to get line items that are not readily visible in other reports.',
    category: 'billing',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'bill-plus-location',
    name: 'Bill Plus Location Report',
    description: 'Provides details about charges and usage for all meters (accounts) associated with your bills and includes the details of a Location. Use this report to get structured data extracts of your bills with location information.',
    category: 'billing',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'charges-by-location',
    name: 'Charges by Location Report',
    description: 'This report shows calendarized, summarized charges to the Location level for all bills.',
    category: 'location',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'consumption-by-location',
    name: 'Consumption by Location Report',
    description: 'This report shows calendarized, summarized consumption to the Location level for all bills.',
    category: 'consumption',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'efficiency',
    name: 'Efficiency Report',
    description: 'Shows monthly unit costs (dollars per kWh), by calendar month, for each location. Lower unit costs indicate higher efficiency. Sorted from least-efficient to most-efficient.',
    category: 'analytics',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'energy-star',
    name: 'Energy Star Meter Reads',
    description: 'This report shows bill data related to Energy Star ratings.',
    category: 'compliance',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'expected-bill',
    name: 'Expected Bill Report',
    description: 'This report will list all bills and the date the system is expecting the associated bills to be available from the vendor.',
    category: 'billing',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'expected-bill-by-account',
    name: 'Expected Bill Report by Account',
    description: 'This report will list all virtual accounts and the date the system is expecting the associated bills to be available from the vendor.',
    category: 'billing',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'location',
    name: 'Location Report',
    description: 'Shows standard location attributes and custom location attributes for each location in the system.',
    category: 'location',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'monthly-summary',
    name: 'Monthly Summary Report',
    description: 'This report shows calendarized, summarized charge, consumption and unit cost to the Location level for all bills.',
    category: 'analytics',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'monthly-summary-pairing',
    name: 'Monthly Summary Report with Pairing',
    description: 'This report shows calendarized, summarized charge, consumption and unit cost to the Location level for all bills.',
    category: 'analytics',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'monthly-usage-detail',
    name: 'Monthly Usage Detail Report',
    description: 'Provides details about charge and usage items for all virtual accounts associated with your bills in a calendarized view. Use this report to get structured, calendarized data extracts of your bills.',
    category: 'consumption',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'monthly-usage-detail-additional',
    name: 'Monthly Usage Detail Report with Additional Details',
    description: 'Provides details about charge and usage items for all virtual accounts associated with your bills in a calendarized view. Use this report to get structured, calendarized data extracts of your bills. This report also provides the information about the Start and End Date, Days of Service, Vendor attributes, Location attributes and Virtual account attributes.',
    category: 'consumption',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'prorated-monthly-usage',
    name: 'Prorated Monthly Usage Details Report',
    description: 'Provides detailed calendar-month usage data for every meter (account) across all your bills, with prorated values when only partial data is available for a month. Use this report when you want to aggregate calendar-month views of all of your energy consumption that spans across bills and billing periods.',
    category: 'consumption',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
  {
    id: 'tableau',
    name: 'Tableau Report',
    description: 'A single report used for output into BI tools such as Tableau. It is based on the Monthly Usage Detail report but includes location information. This report is now not normally used, and instead users download multiple other reports separately to import into Tableau.',
    category: 'analytics',
    deliveryOptions: ['Run Now', 'Schedule'],
    deliveryMethods: ['Email', 'FTP', 'Download']
  },
];

const categoryColors = {
  accounting: { bg: 'bg-blue-50 dark:bg-blue-950/20', border: 'border-blue-500', text: 'text-blue-600' },
  billing: { bg: 'bg-green-50 dark:bg-green-950/20', border: 'border-green-500', text: 'text-green-600' },
  consumption: { bg: 'bg-purple-50 dark:bg-purple-950/20', border: 'border-purple-500', text: 'text-purple-600' },
  location: { bg: 'bg-orange-50 dark:bg-orange-950/20', border: 'border-orange-500', text: 'text-orange-600' },
  analytics: { bg: 'bg-cyan-50 dark:bg-cyan-950/20', border: 'border-cyan-500', text: 'text-cyan-600' },
  compliance: { bg: 'bg-amber-50 dark:bg-amber-950/20', border: 'border-amber-500', text: 'text-amber-600' },
};

export function ReportsLibrary() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [openReports, setOpenReports] = useState<Set<string>>(new Set());
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const categories = useMemo(() => {
    const unique = new Set(reports.map(r => r.category));
    return Array.from(unique);
  }, []);

  const filteredReports = useMemo(() => {
    return reports.filter(report => {
      const matchesSearch = report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           report.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || report.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  const toggleReport = (reportId: string) => {
    const newOpen = new Set(openReports);
    if (newOpen.has(reportId)) {
      newOpen.delete(reportId);
    } else {
      newOpen.add(reportId);
    }
    setOpenReports(newOpen);
  };

  const handleCreateReport = (report: Report) => {
    setSelectedReport(report);
    setShowCreateModal(true);
  };

  const handleDownload = (report: Report) => {
    // Trigger download
    console.log('Downloading report:', report.name);
    // In real implementation, this would call an API to generate and download the report
  };

  return (
    <div className="space-y-6">
      {/* Search and Filter */}
      <Card className="border-l-4 border-l-primary/50">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Filter className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Reports Library</CardTitle>
              <CardDescription>Browse and download standard reports</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search report name and description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Reports List */}
      <div className="space-y-3">
        {filteredReports.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground font-medium">No reports found</p>
              <p className="text-sm text-muted-foreground">Try adjusting your search or filter</p>
            </CardContent>
          </Card>
        ) : (
          filteredReports.map((report) => {
            const isOpen = openReports.has(report.id);
            const categoryColor = categoryColors[report.category];
            
            return (
              <Collapsible
                key={report.id}
                open={isOpen}
                onOpenChange={() => toggleReport(report.id)}
              >
                <Card className={`border-l-4 ${categoryColor.border}`}>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <div className={`p-2 rounded-lg ${categoryColor.bg}`}>
                            <FileText className={`h-5 w-5 ${categoryColor.text}`} />
                          </div>
                          <div className="flex-1">
                            <CardTitle className="text-base flex items-center gap-2">
                              {report.name}
                              <Badge variant="outline" className={categoryColor.border}>
                                {report.category}
                              </Badge>
                            </CardTitle>
                            <CardDescription className="mt-1">
                              {report.description}
                            </CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(report);
                            }}
                            className="gap-2"
                          >
                            <Download className="h-4 w-4" />
                            Download
                          </Button>
                          {isOpen ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="space-y-4 border-t pt-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              Delivery Options
                            </Label>
                            <Select defaultValue="run-now">
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {report.deliveryOptions.map(option => (
                                  <SelectItem key={option} value={option.toLowerCase().replace(' ', '-')}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                              <Settings className="h-4 w-4 text-muted-foreground" />
                              Delivery Methods
                              <Info className="h-3 w-3 text-muted-foreground" />
                            </Label>
                            <Select defaultValue="download">
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {report.deliveryMethods.map(method => (
                                  <SelectItem key={method} value={method.toLowerCase()}>
                                    {method}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                          >
                            <Settings className="h-4 w-4" />
                            Customize
                          </Button>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowCreateModal(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleCreateReport(report)}
                              className="gap-2"
                            >
                              <Download className="h-4 w-4" />
                              Create Report
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })
        )}
      </div>

      {/* Create Report Modal */}
      {showCreateModal && selectedReport && (
        <CreateReportModal
          report={selectedReport}
          open={showCreateModal}
          onOpenChange={setShowCreateModal}
        />
      )}
    </div>
  );
}

